import { useState, useEffect } from 'react'
import {
  useTranslate,
  useRedirect,
  useResourceContext,
  useRefresh,
  useNotify,
  RecordContextProvider,
} from 'react-admin'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { blue } from 'src/assets/colors'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import { useParams } from 'react-router-dom'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'
import { splitPassengerItemReservation } from 'src/components/pages/sales/reservation/helper/splitPassengerFromItemReservation'
import { isUndefined } from 'src/helper/isUndefined'
import { AIR_DETAIL } from 'src/helper/resourceEntity'
import EditIcon from '@mui/icons-material/Edit'
import PriceChangeIcon from '@mui/icons-material/PriceChange'
import { PassengerAirModal } from '../modals/PassengerAirModal'
import { HasPermission } from 'src/helper/HasPermission'
import { PERMISSION_EDIT, PERMISSION_PRICING } from 'src/helper/Permission'
import { INVOICED } from './state'
import { PAX_PERMISSION_ENTITY } from 'src/helper/resourcesByPermission'
import { PricingPassengerModal } from 'src/components/pages/sales/reservation/logic/modals/PricingPassengerModal'
import { paxServices } from 'src/services/reservation/pax/pax.service'
import { customAwait } from 'src/components/pages/sales/reservation/helper/customAwait'

export const Passenger = ({ itemId, passengers, idResource, hashItemReserve = null, ...props }) => {
  const translate = useTranslate()
  const redirect = useRedirect()
  const refresh = useRefresh()
  const notify = useNotify()
  const resourceName = useResourceContext()
  const { id } = useParams()

  const [isSeparatingPassengers, setIsSeparatingPassengers] = useState(false)
  const [selectedPassengers, setSelectedPassengers] = useState([])
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [loadingSplitButton, setLoadingSplitItem] = useState(false)
  const [openPassengerEditModal, setOpenPassengerEditModal] = useState(false)
  const [openPassengerPricingModal, setOpenPassengerPricingModal] = useState(false)
  const [passengerPricingModalLoading, setPassengerPricingModalLoading] = useState(false)
  const [passengerToEdit, setPassengerToEdit] = useState(null)
  const [paxItemToEdit, setPaxItemDataToEdit] = useState(null)
 
  useEffect(() => {
    if (confirm) {
      splitPassengerItemReservation({
        hashItemReserve,
        refresh,
        notify,
        setLoadingSplitItem,
        setConfirm,
        setOpen,
        ids_passenger: selectedPassengers.map((passenger) => passenger.id),
        setIsSeparatingPassengers,
      })
    }
  }, [confirm])

  useEffect(() => {
    if (!isSeparatingPassengers) {
      setSelectedPassengers([])
    }
  }, [isSeparatingPassengers])

  const handleSeparatePassengers = () => {
    setIsSeparatingPassengers(true)
  }

  const handleCancel = () => {
    setIsSeparatingPassengers(false)
    setSelectedPassengers([])
  }

  const handleConfirm = () => {
    setOpen(true)
  }

  const handleSelectPassenger = (event, passenger) => {
    if (event.target.checked) {
      setSelectedPassengers((prev) => [...prev, passenger])
    } else {
      setSelectedPassengers((prev) => prev.filter((p) => p.id !== passenger.id))
    }
  }

  const getExtraColumnValues = (passenger, itemId) => {
    if (props?.resource == AIR_DETAIL) {
      const pax_item = passenger?.pax_item_relation?.filter(
        (pax_relation) => pax_relation?.item_id == itemId && pax_relation?.pax_id == passenger?.id
      )?.[0]
      return (
        <>
          <TableCell>{isUndefined(pax_item?.ticket_number)}</TableCell>
          <TableCell>{isUndefined(pax_item?.ticket_type)}</TableCell>
          <TableCell>
            <Box display={'flex'}>
              {editEticketButton(pax_item, passenger)}
              {editPricingButton(pax_item, passenger)}
            </Box>
          </TableCell>
        </>
      )
    }
  }

  const getExtraColumn = () => {
    if (props?.resource == AIR_DETAIL) {
      return (
        <>
          <TableCell>{translate('ra.field.e_ticket')}</TableCell>
          <TableCell>{translate('ra.field.ticket_type')}</TableCell>
          <TableCell>{translate('ra.field.action')}</TableCell>
        </>
      )
    }
  }

  const editEticketButton = (pax_item, passenger) => {
    return (
      <>
        <HasPermission permission={PERMISSION_EDIT} resource={'pax'}>
          <LoadingButton
            variant='contained'
            size='small'
            color='primary'
            onClick={() => {
              setOpenPassengerEditModal(true)
              setPassengerToEdit(passenger)
              setPaxItemDataToEdit(pax_item)
            }}
            style={{ marginLeft: '10px' }}
          >
            <EditIcon fontSize='small' />
          </LoadingButton>
        </HasPermission>
      </>
    )
  }


  const handleUpdatePassengerPricing = async (record) => {
    try {
      setPassengerPricingModalLoading(true)
      if (record) {
        const status = await paxServices.updatePricingPax(record)
        if (status >= 200 && status <= 204) {
          notify('ra.notification.updated_success_pricing', {
            type: 'success',
          })
          return
        }
      }
      notify('ra.notification.updated_error_pricing', {
        type: 'error',
      })
    } catch (error) {
      notify('ra.notification.updated_error_pricing', {
        type: 'error',
      })
    } finally {
      customAwait([refresh, () => handleClosePricingModal(), () => setPassengerPricingModalLoading(false)])

    }
  }

  const formatPassengerUpdatePricing = (paxItemToEdit, pricing_updated) => {
    try {
      const data = {
        ...paxItemToEdit,
        pricing: { ...paxItemToEdit?.pricing, ...pricing_updated?.pricing },
      }
      return data
    } catch (error) {
      return null
    }
  }

  const getRecordPricing = (pax_item) => {
    try {
      const pricing = pax_item?.pricing
      let record = {
        pricing: {
          comision: 0,
          commission_paid: 0,
          commission_percentage: 0,
          fare_amount: 0,
          over_amount: 0,
          taxes_amount: 0,
          taxes_1: 0,
          taxes_2: 0,
          taxes_3: 0,
          iva: 0,
        },
      }
      if (pricing) {
        record.pricing = { ...record.pricing, ...pricing }
      }
      return record
    } catch (error) {
      return null
    }
  }

  const handleClosePricingModal = () => {
    setOpenPassengerPricingModal(false)
    setPassengerToEdit(null)
    setPaxItemDataToEdit(null)
  }

  const handleCloseEditEticketModal = () => {
    setPassengerToEdit(null)
    setPaxItemDataToEdit(null)
  }
  
  const editPricingButton = (pax_item, passenger) => {
    return (
      <>
        <HasPermission permission={PERMISSION_PRICING} resource={PAX_PERMISSION_ENTITY}>
          <LoadingButton
            variant='contained'
            size='small'
            color='primary'
            onClick={() => {
              setOpenPassengerPricingModal(true)
              setPassengerToEdit(passenger)
              setPaxItemDataToEdit(pax_item)
            }}
            style={{ marginLeft: '10px' }}
          >
            <PriceChangeIcon fontSize='small' />
          </LoadingButton>
        </HasPermission>
      </>
    )
  }
  

  if (passengers && Array.isArray(passengers) && passengers?.length !== 0) {
    return (
      <Box className='passenger_container'>
        <Box style={{ display: 'flex' }}>
          <Box style={{ display: 'flex', alignItems: 'center', flex: 3 }}>
            <Typography variant='h6' component='div' color={blue}>
              {translate('ra.title.passenger')} ({passengers.length})
            </Typography>
            {(id || idResource) && (
              <IconButton
                style={{ color: blue }}
                size='small'
                onClick={() => redirect(`/${resourceName}/${id || idResource}/show/1`)}
              >
                <ArrowForwardIcon />
              </IconButton>
            )}
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
            {isSeparatingPassengers && (
              <>
                <LoadingButton
                  variant='contained'
                  color='error'
                  onClick={handleCancel}
                  style={{ marginLeft: '10px' }}
                >
                  {translate('ra.action.cancel')}
                </LoadingButton>
                <LoadingButton
                  variant='contained'
                  color='success'
                  onClick={handleConfirm}
                  disabled={selectedPassengers.length === 0}
                  style={{ marginLeft: '10px' }}
                >
                  {translate('ra.action.to_confirm')}
                </LoadingButton>
              </>
            )}
            <LoadingButton
              variant='contained'
              color='primary'
              onClick={handleSeparatePassengers}
              disabled={isSeparatingPassengers || passengers?.length <= 1 || props?.status !== INVOICED}
              loading={loadingSplitButton}
              style={{ marginLeft: '10px' }}
            >
              {translate('ra.action.split')}
            </LoadingButton>
          </Box>
        </Box>

        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                {isSeparatingPassengers && <TableCell></TableCell>}
                <TableCell>{translate('ra.field.passenger_type')}</TableCell>
                <TableCell>{translate('ra.field.name')}</TableCell>
                <TableCell>{translate('ra.field.document_type')}</TableCell>
                <TableCell>{translate('ra.field.number')}</TableCell>
                <TableCell>{translate('ra.field.mail')}</TableCell>
                <TableCell>{translate('ra.field.nationality')}</TableCell>
                <TableCell>{translate('ra.field.country')}</TableCell>
                {getExtraColumn()}
              </TableRow>
            </TableHead>
            <TableBody>
              {passengers.map((passenger, index) => (
                <TableRow key={index}>
                  {isSeparatingPassengers && (
                    <TableCell>
                      <Checkbox
                        checked={selectedPassengers.some((p) => p.id === passenger.id)}
                        onChange={(event) => handleSelectPassenger(event, passenger)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{isUndefined(passenger.type)}</TableCell>
                  <TableCell>{`${passenger.name} ${passenger.surname}`}</TableCell>
                  <TableCell>{isUndefined(passenger.document_type_relation?.short_name)} </TableCell>
                  <TableCell>{isUndefined(passenger.document_number)}</TableCell>
                  <TableCell>{isUndefined(passenger.email)}</TableCell>
                  <TableCell>{isUndefined(passenger.nationality)}</TableCell>
                  <TableCell>{isUndefined(passenger.country)}</TableCell>
                  {getExtraColumnValues(passenger, itemId)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <ConfirmationDialog
          title={translate('ra.message.confirm_separation')}
          contentText={
            selectedPassengers.length > 0 ? (
              <div style={{ whiteSpace: 'pre-line' }}>
                {selectedPassengers.map((p, index) => (
                  <Typography key={index} component='div' fontWeight='bold'>
                    - {p?.name} {p?.surname}
                  </Typography>
                ))}
              </div>
            ) : (
              ''
            )
          }
          buttonConfirmText={translate('ra.message.yes')}
          buttonCancel={translate('ra.message.no')}
          setOpen={setOpen}
          setConfirm={setConfirm}
          open={open}
          loadingModalButton={loadingSplitButton}
          styleDialogContentTex={{ whiteSpace: 'pre-line' }}
        />

        {openPassengerPricingModal && (
          <RecordContextProvider value={getRecordPricing(paxItemToEdit)}>
            <PricingPassengerModal
              open={openPassengerPricingModal}
              loadingButton={passengerPricingModalLoading}
              handleClose={() => handleClosePricingModal()}
              onSubmit={(record_updated) => {
                handleUpdatePassengerPricing(formatPassengerUpdatePricing(paxItemToEdit, record_updated))
              }}
              titleModal={translate('ra.title.update_pricing_passenger', {
                passenger: `${passengerToEdit?.name} ${passengerToEdit?.surname}`,
              })}
            />
          </RecordContextProvider>
        )}

        {openPassengerEditModal && (
          <PassengerAirModal
            open={openPassengerEditModal}
            setOpen={setOpenPassengerEditModal}
            pax_item_data={paxItemToEdit || {}}
            passenger={passengerToEdit}
            handleCloseEditEticketModal={handleCloseEditEticketModal}
          />
        )}
      </Box>
    )
  }

  return null
}
